// Extracted verbatim from views/CaseDetail.js (PART C). Imports are the union
// used across the case-detail tabs; unused ones in a given tab are harmless.
import React, { useEffect, useRef, useState } from "react";
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ESTADO_LABEL, PREFER_SUB_TIPO_STATES } from "../../components/Pills";
import { formatTipo } from "../../utils/case_taxonomy";
import { cases, insumos as insumosApi, users as usersApi, moderation as moderationApi } from "../../api/endpoints";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import InsumosTab from "../insumos/InsumosTab";
import LitisoftFields from "../../components/LitisoftFields";
import LitisoftIncompleteModal from "../../components/LitisoftIncompleteModal";
import LitisoftMandatoryInline from "../../components/LitisoftMandatoryInline";
import { TERMINO_OPTIONS, formatTermino } from "../../utils/termino";
import { canMarkContestadaDirectly, canRequestModeration, availableContestadaCategorias, CONTESTADA_LABELS as CONTESTADA_LABELS_FULL, rolesOf } from "../../utils/permissions";
import LawyerActionPanel from "../../components/lawyer/LawyerActionPanel";
import ProcesoCarpetasBanner from "../../components/ProcesoCarpetasBanner";
import CarpetaDocumentsPanel from "../../components/CarpetaDocumentsPanel";
import CarpetaStatusBadge from "../../components/CarpetaStatusBadge";
import InlineEditAccionante from "../../components/InlineEditAccionante";
import TipoFalloEditModal from "../../components/TipoFalloEditModal";
import { PERSONA_EDIT_ROLES } from "../Personas";
import { fmt, InlineEdit, hasLawyerRole, MarcarContestada, CaseFlagsToggles, hasSancionesSpecialization, SancionLifecycle, MarcarInsumoDeficiente, SolicitarModeracion, LawyerAgencyButtons, TABS, CONTESTADA_LABELS, TipoActuacionDisplay } from "./shared";

export default function DecisionTab({ c, user, onChanged, onClose }) {
  const toast = useToast();
  // Sprint X2 — desfavorables can be decided from any pipeline estado.
  // Favorables / Nulidad / NO_PEDIR_INSUMO keep the legacy review-state
  // path (fallo_pendiente_revision / pendiente_revision_impugnacion).
  // Tipo_fallo gate is enforced backend-side in routes/cases.js
  // decide_impugnar handler too — UI here just controls editability.
  const isDesfavorableSubtipo = c.sub_tipo === "primera_desfavorable"
                              || c.sub_tipo === "segunda_desfavorable";
  const PIPELINE_DECIDABLE = isDesfavorableSubtipo
    && ["asignado_abogado","pendiente_insumos","insumos_solicitados",
        "expediente_solicitado","insumos_recibidos"].includes(c.estado);
  const PRE_DECISION = c.estado === "fallo_pendiente_revision"
                    || c.estado === "pendiente_revision_impugnacion"
                    || PIPELINE_DECIDABLE;
  // Multi-role aware (Sprint 54b — revisor_fallos lives in user_roles).
  const userRolesDec = (user?.roles && user.roles.length)
    ? user.roles : (user?.role ? [user.role] : []);
  const isRevisorOrAdmin = userRolesDec.includes("revisor_fallos")
                        || userRolesDec.includes("admin");
  const editable = c.tipo === "fallo" && PRE_DECISION && isRevisorOrAdmin;

  // Read-only echo of an existing decision
  const decided = c.tipo === "fallo" && !PRE_DECISION;

  const [impugnar, setImpugnar]       = useState(null); // null | true | false
  const [reasonChoice, setReasonChoice] = useState(""); // dropdown selection
  const [reason, setReason]           = useState("");   // final string sent to backend
  const [busy, setBusy]               = useState(false);

  async function submit() {
    if (impugnar == null) { toast.push("Seleccione Sí o No", "error"); return; }
    if (impugnar === false && !reason.trim()) {
      toast.push("Indique el motivo para no impugnar", "error");
      return;
    }
    setBusy(true);
    try {
      const payload = { impugnar };
      if (impugnar === false) payload.por_que_no_impugno = reason.trim();
      await cases.action(c.id, "decide_impugnar", payload);
      toast.push("Decisión registrada");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al registrar la decisión", "error");
    } finally { setBusy(false); }
  }

  // ---- Read-only view (already decided) ----
  if (decided || c.impugnacion_decision != null) {
    const yes = !!c.impugnacion_decision;
    return (
      <>
        <h3 style={{ marginTop: 0 }}>Decisión registrada</h3>
        <dl className="kvs">
          <dt>Tipo de fallo</dt>      <dd>{c.tipo_fallo || "—"}</dd>
          <dt>Juzgado</dt>            <dd>{c.juzgado || "—"}</dd>
          <dt>Radicado</dt>           <dd>{c.radicado}</dd>
          <dt>Accionante</dt>         <dd>{c.accionante_nombre || "—"}</dd>
          <dt>Decisión</dt>           <dd>
            <span className={"badge " + (yes ? "mint" : "muted")}>{yes ? "Impugnado" : "No impugnado"}</span>
          </dd>
          {!yes && c.por_que_no_impugno && (
            <>
              <dt>Motivo</dt>
              <dd>{c.por_que_no_impugno}</dd>
            </>
          )}
        </dl>
      </>
    );
  }

  // ---- Editable view ----
  const isNoPedirInsumo = c.tipo_fallo === "NO_PEDIR_INSUMO";
  return (
    <>
      <h3 style={{ marginTop: 0 }}>Decisión sobre impugnación</h3>
      <dl className="kvs">
        <dt>Tipo de fallo</dt>  <dd>{c.tipo_fallo || "—"}</dd>
        <dt>Juzgado</dt>        <dd>{c.juzgado || "—"}</dd>
        <dt>Radicado</dt>       <dd>{c.radicado}</dd>
        <dt>Accionante</dt>     <dd>{c.accionante_nombre || "—"}</dd>
      </dl>

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
        <div className="label-uc" style={{ marginBottom: 8 }}>¿Impugnar?</div>
        <div className="row" style={{ gap: 16 }}>
          <label style={radioLabel}>
            <input type="radio" disabled={!editable} checked={impugnar === true}  onChange={() => setImpugnar(true)} /> Sí
          </label>
          <label style={radioLabel}>
            <input type="radio" disabled={!editable} checked={impugnar === false} onChange={() => setImpugnar(false)} /> No
          </label>
        </div>
      </div>

      {impugnar === true && (
        <div className="warn-inline" style={{ marginTop: 12, background: "var(--accent-mint-bg)", borderColor: "var(--accent-mint)", color: "var(--accent-mint)" }}>
          El caso entrará en la cola de Insumos para preparar la impugnación.
        </div>
      )}
      {impugnar === false && isNoPedirInsumo && (
        <div className="warn-inline" style={{ marginTop: 12, background: "var(--accent-mint-bg)", borderColor: "var(--accent-mint)", color: "var(--accent-mint)" }}>
          El caso será cerrado inmediatamente.
        </div>
      )}

      {impugnar === false && (
        <NoImpugnarPicker
          editable={editable}
          reasonChoice={reasonChoice}
          setReasonChoice={setReasonChoice}
          reason={reason}
          setReason={setReason}
        />
      )}

      {editable && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={submit} disabled={busy || impugnar == null || (impugnar === false && !reason.trim())}>
            {busy && <Spinner color="currentColor" />} Registrar decisión
          </button>
        </div>
      )}
    </>
  );
}

const radioLabel = {
  display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0,
  textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)",
};

const NO_IMPUGNAR_PRESETS = [
  "PCL menor a 10% régimen subsidiado",
  "Derecho de petición - cumplimiento",
  "Realizar PCL - sin insumo",
  "Atención hasta tope SOAT",
  // feature/no-impugnar-motivos-extended — requested by Molly Tafur
  // 2026-05-13. Stored verbatim into case_type_data.por_que_no_impugno
  // (TEXT, no enum). Appended after the existing list, before "Otro".
  "Fallo favorable",
  "Fallo segunda instancia",
  "Derecho de petición sin insumo",
  "Existe orden de pago",
];
const NO_IMPUGNAR_OTRO = "Otro (especificar)";

function NoImpugnarPicker({ editable, reasonChoice, setReasonChoice, reason, setReason }) {
  function onSelect(v) {
    setReasonChoice(v);
    if (v === NO_IMPUGNAR_OTRO) {
      setReason("");
    } else {
      setReason(v); // preset label is the stored motivo
    }
  }
  return (
    <div style={{ marginTop: 12 }}>
      <label>Motivo para no impugnar <span style={{ color: "var(--color-danger)" }}>*</span></label>
      <select
        value={reasonChoice}
        onChange={(e) => onSelect(e.target.value)}
        disabled={!editable}
      >
        <option value="">— elegir —</option>
        {NO_IMPUGNAR_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
        <option value={NO_IMPUGNAR_OTRO}>{NO_IMPUGNAR_OTRO}</option>
      </select>
      {reasonChoice === NO_IMPUGNAR_OTRO && (
        <textarea
          style={{ marginTop: 8 }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Especifique el motivo"
          disabled={!editable}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Documentos tab
// ===========================================================================

